import type { ReactNode } from 'react';
import React, { useMemo, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ActivationTrigger } from '../types';
import { ActivationReason } from '../types';

import { makeGate } from './Gate';
import { Boundary } from './Boundary';
import { useActivationTriggers } from './useActivationTriggers';

type ActivatableComponentProps = {
	children: ReactNode;
	triggers?: ActivationTrigger[];
	onActivate?: (reason: ActivationReason) => void;
	id: string;
	analyticsName?: string;
	ssrSuccess?: boolean;
};

export function ActivatableComponent({
	children,
	triggers,
	onActivate,
	id,
	analyticsName,
	ssrSuccess,
}: ActivatableComponentProps): React.JSX.Element {
	const gate = useMemo(() => makeGate(), []);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const renderChildren = useCallback(() => {
		return children;
	}, [children]);

	const activateComponent = useCallback(
		(reason: ActivationReason) => {
			if (gate?.getStatus() === 'suspended') {
				onActivate?.(reason);

				if (analyticsName) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: 'activatableComponent',
							action: 'activated',
							actionSubject: 'activatableComponent',
							attributes: {
								analyticsName,
								triggers,
								boundaryId: id,
								activationReason: reason,
							},
						},
					}).fire();
				}

				gate.resolve();
			}
		},
		[gate, id, onActivate, createAnalyticsEvent, analyticsName, triggers],
	);

	const hostRefWithActivation = useActivationTriggers({
		triggers,
		gate,
		activateComponent,
		boundaryId: id,
		analyticsName,
	});

	return (
		<div ref={hostRefWithActivation} data-hydration-shield={id}>
			<Boundary
				gate={gate}
				id={id}
				render={renderChildren}
				ssrSuccess={ssrSuccess}
				activateComponent={activateComponent}
				resolved={gate?.getStatus?.() === 'resolved'}
				boundaryName={analyticsName || id}
				triggers={triggers}
			/>
		</div>
	);
}
